import { Button } from "@egaranti/components";
import { useToast } from "@egaranti/components";
import { Tag } from "@egaranti/components";
import { Pagination } from "@egaranti/components";
import { Dialog, DialogContent } from "@egaranti/components";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@egaranti/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@egaranti/components";
import { TableSkeleton } from "@egaranti/components";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import ExportIcon from "../../assets/icons/export.svg?react";
import ImportIcon from "../../assets/icons/import.svg?react";
import PlusIcon from "../../assets/icons/plus.svg?react";

import productActivationService from "@/services/productActivationService";
import productService from "@/services/productService";

import useProductsStore from "@/stores/useProductsStore";

import ProductFilterBar from "@/components/products/ProductFilterBar";
import SendWarrantySheet from "@/components/products/sendWarrantySheet";
import DeleteProductConfirmDialog from "@/components/products/deleteProductConfirmDialog";
import CheckRole from "@/components/shared/checkRole";
import EmptyTable from "@/components/shared/EmptyTable";
import PageSizeSelect from "@/components/shared/pageSizeSelect";
import BulkImportWizard from "@/components/ui/BulkImportWizard";

import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  ChevronRight,
  Copy,
  FileX,
  Link2,
  MoreHorizontal,
  RotateCw,
  Send,
  Trash2,
} from "lucide-react";

const Products = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [exportLoading, setExportLoading] = useState(false);
  const [openSendWarrantySheet, setOpenSendWarrantySheet] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [subdomain, setSubdomain] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const {
    products,
    loading,
    error,
    filters,
    sort,
    getProducts,
    setFilters,
    setFiltersAndFetch,
    toggleSort,
  } = useProductsStore();

  useEffect(() => {
    getProducts();
    productActivationService
      .getProductActivations()
      .then((data) => {
        if (data?.subdomain) {
          setSubdomain(data.subdomain);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // EPS-4850 B-4 fix: setFilters + getProducts race'i için store atomic action.
  const onPageChange = (page) => {
    setFiltersAndFetch({ ...filters, page });
  };

  const onPageSizeChange = (size) => {
    localStorage.setItem("pageSize", size);
    setFiltersAndFetch({ ...filters, size, page: 1 });
  };

  const onClickItem = (e, id) => {
    e.stopPropagation();
    navigate(`/products/products/${id}`);
  };

  const onClickImport = () => {
    setIsBulkUploadOpen(true);
  };

  const bulkCreate = async (data) => {
    return productService.importProductsFromSource("PANEL", data);
  };

  const onClickExport = async () => {
    setExportLoading(true);
    try {
      // BE küçük kataloglarda CSV'yi doğrudan döner; büyük kataloglarda gövdesiz 202
      // dönüp dosyayı maille gönderir.
      const response = await productService.exportProducts(filters);

      if (response?.status === 202 || !response?.data?.size) {
        toast({
          title: t(
            "productsPage.exportSuccess",
            "Ürünler birkaç dakika içerisinde hesabınıza kayıtlı e-posta adresine gönderilecektir.",
          ),
          variant: "success",
        });
        return;
      }

      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "text/csv;charset=utf-8" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "urunler.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        variant: "error",
        title: t("productsPage.exportError", "Dışa aktarma başarısız"),
      });
    } finally {
      setExportLoading(false);
    }
  };

  const onClickSendWarranty = (e, id) => {
    if (e) e.stopPropagation();
    navigate(`?id=${id}`);
    setOpenSendWarrantySheet(true);
  };

  const onConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await productService.deleteProduct(deleteTarget.id);
      toast({
        title: t("productsPage.deleteSuccess", "Ürün silindi"),
        variant: "success",
      });
      setDeleteTarget(null);
      const currentPage = Number(filters.page) || 1;
      if (products?.length === 1 && currentPage > 1) {
        await setFiltersAndFetch({ ...filters, page: currentPage - 1 });
      } else {
        await getProducts();
      }
    } catch (err) {
      toast({
        title:
          err.response?.data?.errors?.[0]?.message ||
          t("productsPage.deleteError", "Ürün silinemedi"),
        variant: "error",
      });
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const copyQRLink = (e, productId) => {
    if (e) e.stopPropagation();
    navigator.clipboard.writeText(
      `https://${subdomain}.egaranti.com.tr/product/${productId}`,
    );
    toast({
      description: t("common.copied", "Kopyalandı"),
      variant: "success",
    });
  };

  // Sort helper
  const getSortDirection = (field) => {
    const sortItem = sort.find((s) => s.field === field);
    return sortItem?.direction || null;
  };

  // Format price
  const formatPrice = (price, unit) => {
    if (!price && price !== 0) return "-";
    const formatter = new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: unit || "TRY",
      minimumFractionDigits: 2,
    });
    return formatter.format(price);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    // dateString format: "22-01-2026 08:55"
    return dateString.split(" ")[0];
  };

  return (
    <main className="min-h-full bg-[#F9FAFC] p-4">
      {/* Top Action Buttons */}
      <div className="flex flex-col gap-4 pb-4 sm:flex-row sm:items-center sm:justify-end sm:gap-4">
        <Button
          className="w-full sm:w-auto"
          disabled={exportLoading}
          variant="secondaryGray"
          onClick={onClickExport}
        >
          <ExportIcon />
          {t("common.export")}
        </Button>
        <CheckRole section="PRODUCTS_LIST" action="EDIT">
          <Button
            className="w-full sm:w-auto"
            variant="secondaryGray"
            onClick={onClickImport}
          >
            <ImportIcon />
            {t("common.import")}
          </Button>
        </CheckRole>
        <CheckRole section="PRODUCTS_LIST" action="EDIT">
          <Button
            className="w-full font-medium sm:w-auto"
            onClick={() => navigate("/products/products/create")}
          >
            <PlusIcon />
            <span>{t("common.addProduct")}</span>
          </Button>
        </CheckRole>
      </div>

      <div className="overflow-hidden rounded-lg">
        <div className="flex flex-col bg-white pb-4">
          {/* Modern Filter Bar */}
          <ProductFilterBar />

          {/* Results Summary Row */}
          <div className="flex items-center justify-between border-b bg-white px-6 py-3">
            <span className="text-sm text-[#677389]">
              {loading ? (
                "..."
              ) : (
                <>
                  <span className="font-semibold text-[#364152]">
                    {filters.totalElements || products.length}
                  </span>{" "}
                  {t("productsPage.productsFound", "ürün bulundu")}
                </>
              )}
            </span>

            <Button
              variant="secondaryColor"
              className="font-medium"
              onClick={() => navigate("/products/sendWarranty")}
            >
              {t("productsPage.sendBulkWarranty")}
            </Button>
          </div>

          {/* List — error / loading / empty / data */}
          {error ? (
            <ErrorState message={error} onRetry={getProducts} />
          ) : loading ? (
            <>
              {/* Desktop skeleton */}
              <div className="hidden md:block">
                <Table>
                  <TableSkeleton colSpan={7} />
                </Table>
              </div>
              {/* Mobile skeleton — stacked cards, not 7-col matrix */}
              <div className="flex flex-col divide-y divide-gray-100 md:hidden">
                {Array.from({ length: 6 }).map((_, i) => (
                  <ProductCardSkeleton key={i} />
                ))}
              </div>
            </>
          ) : products.length ? (
            <>
              {/* Desktop table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <SortableTableHead
                      field="name"
                      label={t("productsPage.productName")}
                      sortDirection={getSortDirection("name")}
                      onSort={toggleSort}
                    />
                    <SortableTableHead
                      field="price"
                      label={t("productsPage.price", "Fiyat")}
                      sortDirection={getSortDirection("price")}
                      onSort={toggleSort}
                    />
                    <SortableTableHead
                      field="warrantyDuration"
                      label={t("productsPage.warrantyDuration", "Garanti Süresi")}
                      sortDirection={getSortDirection("warrantyDuration")}
                      onSort={toggleSort}
                    />
                    <TableHead>{t("productsPage.category")}</TableHead>
                    <TableHead>{t("productsPage.warrantyDocument")}</TableHead>
                    <SortableTableHead
                      field="createdAt"
                      label={t("productsPage.createdAt", "Eklenme Tarihi")}
                      sortDirection={getSortDirection("createdAt")}
                      onSort={toggleSort}
                    />
                    <TableHead className="w-12"></TableHead>
                  </TableHeader>
                  <TableBody>
                    {products?.map((product) => (
                      <TableRow
                        key={product.id}
                        className="cursor-pointer transition-colors hover:bg-[#F9FAFB]"
                        onClick={(e) => onClickItem(e, product.id)}
                      >
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium text-[#111729]">
                              {product.name}
                            </span>
                            {product.model && (
                              <span className="text-xs text-[#677389]">
                                {product.model}
                              </span>
                            )}
                            {product.productCode && !product.model && (
                              <span className="text-xs text-[#677389]">
                                {product.productCode.length > 20
                                  ? `${product.productCode.slice(0, 20)}...`
                                  : product.productCode}
                              </span>
                            )}
                          </div>
                        </TableCell>

                        <TableCell>
                          <span className="font-medium text-[#111729]">
                            {formatPrice(product.price, product.priceUnit)}
                          </span>
                        </TableCell>

                        <TableCell>
                          <Tag size="sm" variant="blue">
                            {product.warrantyDuration} {t("common.months", "ay")}
                          </Tag>
                        </TableCell>

                        <TableCell>
                          <Tag size="sm">
                            {product.categoryName ||
                              product.mainCategoryName ||
                              "-"}
                          </Tag>
                        </TableCell>

                        <TableCell>
                          <WarrantyDocumentCell product={product} />
                        </TableCell>

                        <TableCell>
                          <span className="text-sm text-[#677389]">
                            {formatDate(product.createdAt)}
                          </span>
                        </TableCell>

                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="w-48 bg-white"
                            >
                              <DropdownMenuItem
                                onClick={() =>
                                  onClickSendWarranty(null, product.id)
                                }
                                className="cursor-pointer"
                              >
                                <Send className="mr-2 h-4 w-4" />
                                {t("productsPage.sendWarranty")}
                              </DropdownMenuItem>
                              {subdomain && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={(e) => copyQRLink(e, product.id)}
                                    className="cursor-pointer"
                                  >
                                    <Copy className="mr-2 h-4 w-4" />
                                    {t(
                                      "productsPage.copyQRLink",
                                      "QR Link Kopyala",
                                    )}
                                  </DropdownMenuItem>
                                </>
                              )}
                              <CheckRole section="PRODUCTS_LIST" action="EDIT">
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => setDeleteTarget(product)}
                                  className="cursor-pointer text-red-600 focus:text-red-600"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  {t("productsPage.delete", "Sil")}
                                </DropdownMenuItem>
                              </CheckRole>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile card list */}
              <div className="flex flex-col divide-y divide-gray-100 md:hidden">
                {products?.map((product) => (
                  <ProductCardMobile
                    key={product.id}
                    product={product}
                    formatPrice={formatPrice}
                    formatDate={formatDate}
                    onClick={() => navigate(`/products/products/${product.id}`)}
                    onSendWarranty={() => onClickSendWarranty(null, product.id)}
                    onCopyQRLink={(e) => copyQRLink(e, product.id)}
                    onDelete={() => setDeleteTarget(product)}
                    showQR={!!subdomain}
                  />
                ))}
              </div>
            </>
          ) : (
            <EmptyTable
              title={t("productsPage.productCouldNotFound")}
              desc={t("productsPage.productCouldNotFoundDesc")}
            />
          )}

          {/* Pagination */}
          <div className="flex flex-col items-center">
            {filters.totalPages > 1 && (
              <Pagination
                totalPages={filters.totalPages}
                currentPage={filters.page}
                pageSize={filters.size}
                onPageChange={(page) => onPageChange(page)}
              />
            )}
            {!loading && (
              <PageSizeSelect
                onPageSizeChange={onPageSizeChange}
                value={filters.size}
              />
            )}
          </div>
        </div>
      </div>

      <SendWarrantySheet
        open={openSendWarrantySheet}
        onOpenChange={setOpenSendWarrantySheet}
      />

      <DeleteProductConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeleteTarget(null);
        }}
        onConfirm={onConfirmDelete}
        loading={deleting}
      />

      <Dialog open={isBulkUploadOpen} onOpenChange={setIsBulkUploadOpen}>
        <DialogContent className="h-full min-w-full bg-white">
          <BulkImportWizard
            title={t("clientsPage.importSheet.importProduct")}
            description="Panel kaynağından ürün içe aktarma"
            templateUrl="https://cdn.egaranti.com/static/product_import.csv?v=3"
            expectedColumns={[
              {
                key: "name",
                label: "Ürün Adı (Max 255 Karakter)",
                required: true,
              },
              {
                key: "productCode",
                label: "Ürün Kodu",
                required: false,
              },
              {
                key: "model",
                label: "Model",
                required: false,
              },
              {
                key: "price",
                label: "Fiyat",
                required: false,
                pattern: {
                  regex: /^[0-9]+(\.[0-9]+)?$/,
                  message: "Geçersiz değer",
                },
              },
              {
                key: "priceUnit",
                label: "Para Birimi (TRY,EUR,USD)",
                required: false,
                pattern: {
                  regex: /^(TRY|EUR|USD)$/i,
                  message: "Geçersiz değer",
                },
              },
              {
                key: "warrantyDuration",
                label: "Garanti Süresi (Ay)",
                required: true,
                pattern: {
                  regex: /^[0-9]+$/,
                  message: "Geçersiz değer",
                },
              },
              {
                key: "mainCategoryName",
                label: "Ana Kategori",
                required: true,
              },
              {
                key: "categoryName",
                label: "Kategori",
                required: true,
              },
              {
                key: "subCategoryName",
                label: "Alt Kategori",
                required: true,
              },
              {
                key: "brandName",
                label: "Marka",
                required: true,
              },
              {
                key: "setupRequired",
                label: "Kurulum Gerekli (true,false)",
                required: false,
                pattern: {
                  regex: /^(true|false|)$/i,
                  message: "Geçersiz değer",
                },
              },
              {
                key: "additionalService",
                label: "Ek Hizmet (true,false)",
                required: false,
                pattern: {
                  regex: /^(true|false|)$/i,
                  message: "Geçersiz değer",
                },
              },
            ]}
            onComplete={(data) => {
              bulkCreate(data)
                .then(() => {
                  setIsBulkUploadOpen(false);
                  toast({
                    title: t("clientsPage.importSheet.success"),
                    variant: "success",
                  });
                  getProducts();
                  navigate("/products/products");
                })
                .catch((error) => {
                  console.error("Error bulk creating products:", error);
                  toast({
                    variant: "error",
                    description:
                      error.response?.data?.errors[0]?.message ||
                      "Import işlemi sırasında hata oluştu",
                  });
                });
            }}
          />
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default Products;

// Sortable Table Head Component
const SortableTableHead = ({ field, label, sortDirection, onSort }) => {
  return (
    <TableHead
      className="cursor-pointer select-none transition-colors hover:bg-[#F9FAFB]"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1.5">
        <span>{label}</span>
        <span className="text-[#9AA4B2]">
          {sortDirection === "ASC" ? (
            <ArrowUp className="h-4 w-4 text-[#0049E6]" />
          ) : sortDirection === "DESC" ? (
            <ArrowDown className="h-4 w-4 text-[#0049E6]" />
          ) : (
            <ArrowUpDown className="h-4 w-4" />
          )}
        </span>
      </div>
    </TableHead>
  );
};

// Mobile Product Card
const ProductCardMobile = ({
  product,
  formatPrice,
  formatDate,
  onClick,
  onSendWarranty,
  onCopyQRLink,
  onDelete,
  showQR,
}) => {
  const { t } = useTranslation();
  const subtitle = product.model || product.productCode || null;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
      className="flex items-start gap-3 bg-white px-4 py-3 transition-colors hover:bg-[#F9FAFB] active:bg-[#F2F5F9]"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium text-[#111729]">
              {product.name}
            </p>
            {subtitle && (
              <p className="truncate text-xs text-[#677389]">{subtitle}</p>
            )}
          </div>
          <span className="whitespace-nowrap text-sm font-semibold text-[#111729]">
            {formatPrice(product.price, product.priceUnit)}
          </span>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <Tag size="sm" variant="blue">
            {product.warrantyDuration} {t("common.months", "ay")}
          </Tag>
          {(product.categoryName || product.mainCategoryName) && (
            <Tag size="sm">
              {product.categoryName || product.mainCategoryName}
            </Tag>
          )}
          {product.warrantyDocumentId ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#DCFAE6] px-2 py-0.5 text-[11px] font-medium text-[#054F31]">
              <Check className="h-3 w-3" />
              {t("productsPage.documentAttached", "Belge")}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#FEF3F2] px-2 py-0.5 text-[11px] font-medium text-[#7A271A]">
              <FileX className="h-3 w-3" />
              {t("productsPage.noWarrantyDocument", "Belge Yok")}
            </span>
          )}
          <span className="ml-auto text-[11px] text-[#9AA4B2]">
            {formatDate(product.createdAt)}
          </span>
        </div>
      </div>

      <div onClick={(e) => e.stopPropagation()} className="shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-11 w-11 p-0"
              aria-label={t("productsPage.actions", "İşlemler")}
            >
              <MoreHorizontal className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 bg-white">
            <DropdownMenuItem
              onClick={onSendWarranty}
              className="cursor-pointer"
            >
              <Send className="mr-2 h-4 w-4" />
              {t("productsPage.sendWarranty")}
            </DropdownMenuItem>
            {showQR && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onCopyQRLink}
                  className="cursor-pointer"
                >
                  <Copy className="mr-2 h-4 w-4" />
                  {t("productsPage.copyQRLink", "QR Link Kopyala")}
                </DropdownMenuItem>
              </>
            )}
            <CheckRole section="PRODUCTS_LIST" action="EDIT">
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onDelete}
                className="cursor-pointer text-red-600 focus:text-red-600"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t("productsPage.delete", "Sil")}
              </DropdownMenuItem>
            </CheckRole>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

// Mobile skeleton card — height matches ProductCardMobile to avoid CLS
const ProductCardSkeleton = () => (
  <div className="flex h-[92px] items-start gap-3 bg-white px-4 py-3">
    <div className="min-w-0 flex-1 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="h-4 w-1/2 animate-pulse rounded bg-gray-200" />
        <div className="h-4 w-16 animate-pulse rounded bg-gray-200" />
      </div>
      <div className="h-3 w-1/3 animate-pulse rounded bg-gray-100" />
      <div className="flex items-center gap-1.5">
        <div className="h-5 w-12 animate-pulse rounded-full bg-gray-100" />
        <div className="h-5 w-16 animate-pulse rounded-full bg-gray-100" />
        <div className="ml-auto h-3 w-14 animate-pulse rounded bg-gray-100" />
      </div>
    </div>
    <div className="h-9 w-9 shrink-0 animate-pulse rounded bg-gray-100" />
  </div>
);

// Error state with retry
const ErrorState = ({ message, onRetry }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FEF3F2]">
        <AlertCircle className="h-6 w-6 text-[#D92D20]" />
      </div>
      <div className="space-y-1">
        <h3 className="font-semibold text-[#111729]">
          {t("productsPage.loadError", "Ürünler yüklenemedi")}
        </h3>
        <p className="max-w-sm text-sm text-[#677389]">{message}</p>
      </div>
      <Button variant="secondaryGray" onClick={onRetry} className="mt-2">
        <RotateCw className="mr-2 h-4 w-4" />
        {t("common.retry", "Tekrar dene")}
      </Button>
    </div>
  );
};

// Warranty Document Cell Component
const WarrantyDocumentCell = ({ product }) => {
  const { t } = useTranslation();

  if (product.warrantyDocumentId) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#DCFAE6]">
          <Check className="h-4 w-4 text-[#079455]" />
        </div>
        <span className="text-sm text-[#111729]">
          {product.warrantyDocumentName ||
            t("productsPage.documentAttached", "Belge Mevcut")}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#FEF3F2]">
        <FileX className="h-4 w-4 text-[#D92D20]" />
      </div>
      <span className="text-sm text-[#667085]">
        {t("productsPage.noWarrantyDocument")}
      </span>
    </div>
  );
};
